import { MathUtils, Quaternion, Vector3 } from 'three';

export const clamp = MathUtils.clamp;
export const lerp = MathUtils.lerp;

/** Frame-rate independent exponential smoothing factor. `halfLife` in seconds. */
export function dampFactor(halfLife: number, dt: number): number {
  return halfLife <= 0 ? 1 : 1 - Math.pow(0.5, dt / halfLife);
}

export function damp(current: number, target: number, halfLife: number, dt: number): number {
  return lerp(current, target, dampFactor(halfLife, dt));
}

/**
 * Wraps an angle in radians into (-pi, pi]. Exactly +-pi (and odd multiples) map to +pi.
 * Meant for angles of modest magnitude (|a| well under 1e6); -0 stays -0.
 */
export function wrapAngle(a: number): number {
  const TWO_PI = Math.PI * 2;
  a %= TWO_PI;
  if (a > Math.PI) a -= TWO_PI;
  else if (a <= -Math.PI) a += TWO_PI;
  return a;
}

/** Interpolates from angle `a` to `b` by `t` along the short way round; the result is not re-wrapped. */
export function lerpAngle(a: number, b: number, t: number): number {
  return a + wrapAngle(b - a) * t;
}

/** Yaw (rotation around +Y) of a quaternion, radians; 0 = looking toward -Z. */
export function yawOf(q: Quaternion): number {
  const fwd = new Vector3(0, 0, -1).applyQuaternion(q);
  return Math.atan2(-fwd.x, -fwd.z);
}

export const UP = Object.freeze(new Vector3(0, 1, 0)) as Vector3;
export const FORWARD = Object.freeze(new Vector3(0, 0, -1)) as Vector3;
